import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { authResetApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import HQMark from "@/components/HQMark";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await authResetApi.forgot(email.trim().toLowerCase());
      setSent(true);
    } catch (err) {
      // Rate-limit response (429) is the only one we surface — any
      // other error we also treat as "sent" so we don't leak account
      // existence.
      if (err.response?.status === 429) {
        toast.error(err.response.data?.detail || "Too many attempts. Please wait and try again.");
      } else {
        setSent(true);
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full bg-[#0B0E14] text-white flex items-center justify-center px-6"
         data-testid="forgot-password-page">
      <form onSubmit={submit} className="w-full max-w-sm space-y-6">
        <div className="flex items-center gap-3 text-white mb-6">
          <HQMark size={36} />
          <span className="font-display text-base font-semibold tracking-tight">Alias HQ</span>
        </div>
        <div>
          <div className="text-eyebrow mb-3">Account recovery</div>
          <h2 className="font-display text-2xl font-semibold tracking-tight">Reset your password</h2>
          <p className="text-white/50 mt-2 text-sm">
            Enter the email tied to your account. We'll send you a single-use link to set a new password.
          </p>
        </div>
        {!sent ? (
          <>
            <div>
              <Label htmlFor="email" className="text-white/70 text-xs uppercase tracking-wider">Email</Label>
              <Input id="email" type="email" required value={email}
                     onChange={(e) => setEmail(e.target.value)}
                     data-testid="forgot-email-input"
                     className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11" />
            </div>
            <Button type="submit" disabled={loading}
                    className="w-full h-11 rounded-full bg-white text-black hover:bg-white/90 font-medium"
                    data-testid="forgot-submit-btn">
              {loading ? "Sending…" : "Send reset link"}
            </Button>
          </>
        ) : (
          <div className="p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5 text-sm text-emerald-200"
               data-testid="forgot-sent-notice">
            If an account exists for that email, a reset link is on its way. The link expires in 60 minutes.
          </div>
        )}
        <div className="text-center">
          <Link to="/login" className="text-xs text-white/50 hover:text-white underline underline-offset-4">
            Back to sign in
          </Link>
        </div>
      </form>
    </div>
  );
}
